import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useComponentDefaults } from "../../Provider";
import { clsx } from "../../utils/clsx";
import s from "./Tooltip.module.css";

const PLACEMENTS = [
  "top","bottom","left","right",
  "topLeft","topRight","bottomLeft","bottomRight",
  "leftTop","leftBottom","rightTop","rightBottom"
];

export default function Tooltip(props) {
  const merged = useComponentDefaults('Tooltip', props, {
    arrow: false,
    autoAdjustOverflow: true,
    defaultOpen: false,
    destroyOnHidden: false,
    fresh: false,
    mouseEnterDelay: 0.1,
    mouseLeaveDelay: 0.1,
    placement: 'top',
    trigger: 'hover',
  });
  const {
    content,
    children,
    className,
    style,
    align,
    arrow = true,
    autoAdjustOverflow = true,
    color,
    defaultOpen = false,
    destroyOnHidden = false,
    fresh = false,
    getPopupContainer,
    mouseEnterDelay = 0.1,
    mouseLeaveDelay = 0.1,
    placement = 'top',
    trigger = 'hover',
    open: openProp,
    zIndex,
    onOpenChange,
    tipClassName,
    tipStyle,
    triggerStyle,
  } = merged;
  const wrapRef = useRef(null);
  const tipRef = useRef(null);

  const [internalOpen, setInternalOpen] = useState(!!defaultOpen);
  const isControlled = typeof openProp === "boolean";
  const open = isControlled ? !!openProp : internalOpen;

  const [curPlacement, setCurPlacement] = useState(PLACEMENTS.includes(placement) ? placement : "top");
  const enterTimer = useRef();
  const leaveTimer = useRef();

  const container = useMemo(() => {
    if (typeof document === "undefined") return null;
    try {
      if (typeof getPopupContainer === "function") return getPopupContainer(wrapRef.current) || document.body;
    } catch {}
    return document.body;
  }, [getPopupContainer]);

  const setOpen = useCallback((v) => {
    if (!isControlled) setInternalOpen(v);
    onOpenChange && onOpenChange(v);
  }, [isControlled, onOpenChange]);

  const clearTimers = () => {
    window.clearTimeout(enterTimer.current);
    window.clearTimeout(leaveTimer.current);
  };

  const handleEnter = () => {
    clearTimers();
    enterTimer.current = window.setTimeout(() => setOpen(true), mouseEnterDelay * 1000);
  };
  const handleLeave = () => {
    clearTimers();
    leaveTimer.current = window.setTimeout(() => setOpen(false), mouseLeaveDelay * 1000);
  };

  const handleClick = () => setOpen(!open);
  const handleContextMenu = (e) => { e.preventDefault(); setOpen(true); };
  const handleFocus = () => setOpen(true);
  const handleBlur = () => setOpen(false);

  // Positioning
  const computePosition = useCallback(() => {
    const host = wrapRef.current;
    const tip = tipRef.current;
    if (!host || !tip) return;

    const rect = host.getBoundingClientRect();
    const tRect = tip.getBoundingClientRect();
    const margin = 8;
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth || 0;
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;

    const toTitleCase = (value) => (value ? `${value.charAt(0).toUpperCase()}${value.slice(1)}` : '');
    const resolvePlacement = (value) => (PLACEMENTS.includes(value) ? value : 'top');
    const clamp = (val, min, max) => {
      if (Number.isNaN(val)) return min;
      if (min > max) return Math.min(Math.max(val, max), min);
      return Math.min(Math.max(val, min), max);
    };

    const computeForPlacement = (targetPlacement) => {
      const nextPlacement = resolvePlacement(targetPlacement);
      const primary = nextPlacement.split(/[A-Z]/)[0];
      const alignPart = nextPlacement.replace(primary, '').toLowerCase();
      const centerX = rect.left + rect.width / 2 - tRect.width / 2;
      const centerY = rect.top + rect.height / 2 - tRect.height / 2;
      let top = centerY;
      let left = centerX;

      if (primary === 'top') {
        top = rect.top - tRect.height - margin;
        left = centerX;
        if (alignPart === 'left') left = rect.left;
        if (alignPart === 'right') left = rect.right - tRect.width;
      } else if (primary === 'bottom') {
        top = rect.bottom + margin;
        left = centerX;
        if (alignPart === 'left') left = rect.left;
        if (alignPart === 'right') left = rect.right - tRect.width;
      } else if (primary === 'left') {
        left = rect.left - tRect.width - margin;
        top = centerY;
        if (alignPart === 'top') top = rect.top;
        if (alignPart === 'bottom') top = rect.bottom - tRect.height;
      } else if (primary === 'right') {
        left = rect.right + margin;
        top = centerY;
        if (alignPart === 'top') top = rect.top;
        if (alignPart === 'bottom') top = rect.bottom - tRect.height;
      }

      return { top, left, primary, alignPart, placementKey: nextPlacement };
    };

    const getOpposite = (primary) => {
      switch (primary) {
        case 'top':
          return 'bottom';
        case 'bottom':
          return 'top';
        case 'left':
          return 'right';
        case 'right':
          return 'left';
        default:
          return primary;
      }
    };

    const getPrimaryOverflow = (position) => {
      const { top, left, primary: posPrimary } = position;
      if (posPrimary === 'top') return Math.max(margin - top, 0);
      if (posPrimary === 'bottom') return Math.max(top + tRect.height - (viewportHeight - margin), 0);
      if (posPrimary === 'left') return Math.max(margin - left, 0);
      if (posPrimary === 'right') return Math.max(left + tRect.width - (viewportWidth - margin), 0);
      return 0;
    };

    const clampToViewport = (position) => {
      const maxLeft = Math.max(viewportWidth - tRect.width - margin, margin);
      const maxTop = Math.max(viewportHeight - tRect.height - margin, margin);
      const minLeft = Math.min(margin, maxLeft);
      const minTop = Math.min(margin, maxTop);
      return {
        ...position,
        top: clamp(position.top, minTop, maxTop),
        left: clamp(position.left, minLeft, maxLeft),
      };
    };

    let current = computeForPlacement(placement);

    if (autoAdjustOverflow) {
      const overflow = getPrimaryOverflow(current);
      if (overflow > 0) {
        const oppositePrimary = getOpposite(current.primary);
        const flippedPlacement = `${oppositePrimary}${toTitleCase(current.alignPart)}`;
        const flipped = computeForPlacement(flippedPlacement);
        const flippedOverflow = getPrimaryOverflow(flipped);
        if (flippedOverflow < overflow) {
          current = flipped;
        }
      }
      current = clampToViewport(current);
    }

    tip.style.top = `${Math.round(current.top)}px`;
    tip.style.left = `${Math.round(current.left)}px`;
    setCurPlacement(current.placementKey);
  }, [placement, autoAdjustOverflow]);

  useEffect(() => {
    if (!open) return;
    computePosition();
    const handler = () => computePosition();
    window.addEventListener('scroll', handler, true);
    window.addEventListener('resize', handler);
    return () => {
      window.removeEventListener('scroll', handler, true);
      window.removeEventListener('resize', handler);
    };
  }, [open, computePosition]);

  useEffect(() => () => { clearTimers(); }, []);

  const triggers = Array.isArray(trigger) ? trigger : [trigger];
  const triggerProps = {};
  if (triggers.includes('hover')) {
    triggerProps.onMouseEnter = handleEnter;
    triggerProps.onMouseLeave = handleLeave;
  }
  if (triggers.includes('click')) {
    triggerProps.onClick = handleClick;
    // Close when focus leaves the trigger after click and on Escape
    triggerProps.onBlur = () => setOpen(false);
    triggerProps.onKeyDown = (e) => { if (e.key === 'Escape') setOpen(false); };
  }
  if (triggers.includes('focus')) {
    triggerProps.onFocus = handleFocus;
    triggerProps.onBlur = handleBlur;
  }
  if (triggers.includes('contextMenu')) {
    triggerProps.onContextMenu = handleContextMenu;
  }

  // Always close on blur/tab-away even if trigger is hover
  if (!triggerProps.onBlur) {
    triggerProps.onBlur = handleBlur;
  }

  // Close on click-away (outside trigger and tooltip)
  useEffect(() => {
    if (!open) return;
    const onDocMouseDown = (e) => {
      const t = e.target;
      const host = wrapRef.current;
      const tip = tipRef.current;
      if (!host || !tip) return;
      if (host.contains(t) || tip.contains(t)) return; // inside -> ignore
      setOpen(false);
    };
    const onFocusIn = (e) => {
      const t = e.target;
      const host = wrapRef.current;
      const tip = tipRef.current;
      if (!host || !tip) return;
      if (host.contains(t) || tip.contains(t)) return; // focus stayed within
      setOpen(false);
    };
    const onKeyDown = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDocMouseDown, true);
    document.addEventListener('focusin', onFocusIn, true);
    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown, true);
      document.removeEventListener('focusin', onFocusIn, true);
      document.removeEventListener('keydown', onKeyDown, true);
    };
  }, [open, setOpen]);

  const arrowEnabled = !!arrow;
  const pointAtCenter = typeof arrow === 'object' ? !!arrow.pointAtCenter : true;

  // Color mapping: accept token names like 'gray-10', 'sky-6', 'brand', or CSS variables via var(...)
  const { bgVar, fgVar } = useMemo(() => {
    if (!color) return { bgVar: undefined, fgVar: undefined };
    let varRef;
    if (typeof color === 'string' && color.startsWith('var(')) {
      varRef = color;
    } else if (typeof color === 'string') {
      // map token key -> var(--token)
      const key = color.trim();
      varRef = `var(--${key})`;
    }
    // Foreground contrast heuristic by scale
    let fg;
    const m = typeof color === 'string' && color.match(/^(gray|sky)-(\d{1,2})$/);
    if (m) {
      const idx = parseInt(m[2], 10);
      fg = idx <= 5 ? 'var(--text)' : '#fff';
    } else if (typeof color === 'string' && /^(brand|accent-|info|success|warning|error)/.test(color)) {
      fg = '#fff';
    }
    return { bgVar: varRef, fgVar: fg };
  }, [color]);

  const tipNode = (
    <span
      ref={tipRef}
      className={clsx(s.tip, tipClassName)}
      role="tooltip"
      data-hidden={!open}
      data-placement={curPlacement}
      data-arrow={arrowEnabled ? 'true' : 'false'}
      style={{
        '--tip-bg': bgVar || color || undefined,
        '--tip-fg': fgVar || undefined,
        '--tip-z': zIndex || undefined,
        position: 'fixed',
        ...(style || {}),
        ...(tipStyle || {}),
      }}
      aria-hidden={!open}
    >
      {arrowEnabled ? <span className={s.arrow} aria-hidden /> : null}
      {fresh ? content : content}
    </span>
  );

  const wrappedChild = (
    <span
      ref={wrapRef}
      className={clsx(s.wrap, className)}
      style={triggerStyle}
      {...triggerProps}
    >
      {children}
    </span>
  );

  return (
    <>
      {wrappedChild}
      {(!open && destroyOnHidden) ? null : (container ? createPortal(tipNode, container) : tipNode)}
    </>
  );
}
